import {
  ChangeEvent,
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services, loadAllPages } from "@/services";
import type {
  FarmerAdvanceCreateInput,
  FarmerAdvanceDisburseInput,
  FarmerAdvanceRecoveryCreateInput,
} from "@/services";
import type {
  CommercialTransaction,
  FarmDetail,
  FarmerAdvance,
  FarmerAdvanceRecovery,
  FarmerAdvanceRecoverySource,
  FarmerDetail,
  HarvestSeason,
} from "@/types/domain";
import { useRoleSimulator } from "@/security/RoleSimulatorProvider";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

const ADVANCE_STATUS_COLORS: Record<string, string> = {
  DRAFT: "border-slate-500/20 bg-slate-500/10 text-slate-600",
  APPROVED: "border-indigo-500/20 bg-indigo-500/10 text-indigo-600",
  DISBURSED: "border-sky-500/20 bg-sky-500/10 text-sky-600",
  PARTIALLY_RECOVERED: "border-amber-500/20 bg-amber-500/10 text-amber-600",
  RECOVERED: "border-green-500/20 bg-green-500/10 text-green-600",
  CANCELLED: "border-slate-500/20 bg-slate-500/10 text-slate-600",
};

const STATUS_FILTERS = ["ALL", "DRAFT", "APPROVED", "DISBURSED", "PARTIALLY_RECOVERED", "RECOVERED", "CANCELLED"];

const RECOVERY_SOURCES: { value: FarmerAdvanceRecoverySource; label: string }[] = [
  { value: "SETTLEMENT", label: "Settlement deduction" },
  { value: "MANUAL_REPAYMENT", label: "Manual repayment" },
];

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

const todayLocalIso = (): string => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const formatDate = (value?: string): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString("en-GB");
};

const formatDateTime = (value?: string): string => {
  if (!value) return "Not recorded";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString("en-GB");
};

const newIdempotencyKey = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `key-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const isPositiveMoney = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return false;
  const amount = Number(trimmed);
  return Number.isFinite(amount) && amount > 0 && amount <= 100_000_000_000;
};

const isValidLocalDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  );
};

const emptyCreateForm = () => ({
  advance_reference: "",
  farmer_id: "",
  farm_id: "",
  harvest_season_id: "",
  currency: "UGX",
  principal_amount: "",
  purpose: "",
  notes: "",
});

const emptyRecoveryForm = () => ({
  recovery_reference: "",
  recovery_date: todayLocalIso(),
  amount: "",
  recovery_source: "SETTLEMENT" as FarmerAdvanceRecoverySource,
  settlement_id: "",
  notes: "",
});

interface ModalProps {
  title: string;
  description?: string;
  testId: string;
  onClose: () => void;
  busy?: boolean;
  children: ReactNode;
}

function Modal({ title, description, testId, onClose, busy = false, children }: ModalProps) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [busy, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <Card
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        className="max-h-[90vh] w-full max-w-2xl overflow-auto"
        onClick={(event: MouseEvent) => event.stopPropagation()}
      >
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">{title}</h2>
              {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={busy} aria-label={`Close ${title}`}>
              Close
            </Button>
          </div>
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

export interface FarmerAdvancesTabProps {
  /** Called after a recovery is recorded so the parent can refresh farmer balances locally without reloading every Accounts dataset. */
  onBalanceChange?: (farmerId: string) => void | Promise<void>;
}

export function FarmerAdvancesTab({ onBalanceChange }: FarmerAdvancesTabProps) {
  const { can } = useRoleSimulator();
  const readable = can("accounts.view") || can("payment.view") || can("transactions.view");
  const canPrepare = can("payment.prepare");
  const canApprove = can("payment.approve");

  const [advances, setAdvances] = useState<FarmerAdvance[]>();
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [farmers, setFarmers] = useState<FarmerDetail[]>([]);
  const [farms, setFarms] = useState<FarmDetail[]>([]);
  const [seasons, setSeasons] = useState<HarvestSeason[]>([]);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [referenceError, setReferenceError] = useState("");

  const [settlements, setSettlements] = useState<CommercialTransaction[]>([]);
  const [settlementsError, setSettlementsError] = useState("");
  const settlementsLoadedRef = useRef(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FarmerAdvance>();
  const [history, setHistory] = useState<FarmerAdvanceRecovery[]>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const detailRequestGenerationRef = useRef(0);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const createKeyRef = useRef<string | null>(null);
  const createSignatureRef = useRef("");

  const [disburseTarget, setDisburseTarget] = useState<FarmerAdvance>();
  const [disburseForm, setDisburseForm] = useState({ payment_method: "MOBILE_MONEY", external_payment_reference: "" });
  const [disbursing, setDisbursing] = useState(false);
  const disbursingRef = useRef(false);

  const [cancelTarget, setCancelTarget] = useState<FarmerAdvance>();
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const cancellingRef = useRef(false);

  const [recoverTarget, setRecoverTarget] = useState<FarmerAdvance>();
  const [recoverForm, setRecoverForm] = useState(emptyRecoveryForm);
  const [recoverError, setRecoverError] = useState("");
  const [recovering, setRecovering] = useState(false);
  const recoveringRef = useRef(false);
  const recoverKeyRef = useRef<string | null>(null);
  const recoverSignatureRef = useRef("");

  const [busyAction, setBusyAction] = useState("");
  const actionRef = useRef(false);

  const loadAdvances = useCallback(async () => {
    setLoadError("");
    try {
      setAdvances(await loadAllPages((options) => services.accounts.listFarmerAdvances(options)));
    } catch (reason: unknown) {
      setLoadError(reason instanceof Error ? reason.message : "Farmer advances could not be loaded.");
    }
  }, []);

  const reloadAdvances = useCallback(async () => {
    try {
      setAdvances(await loadAllPages((options) => services.accounts.listFarmerAdvances(options)));
      setLoadError("");
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Farmer advances could not be refreshed.");
    }
  }, []);

  const loadReferenceData = useCallback(async () => {
    setReferenceLoading(true);
    setReferenceError("");
    try {
      const [farmerRows, farmRows, seasonRows] = await Promise.all([
        loadAllPages((options) => services.farmers.list(options)),
        loadAllPages((options) => services.farms.list(options)),
        services.harvest.listSeasons(),
      ]);
      setFarmers(
        farmerRows.filter(
          (farmer) => farmer.status === "Verified" && farmer.farmerStatus === "ACTIVE" && farmer.recordStatus === "APPROVED",
        ),
      );
      setFarms(farmRows);
      setSeasons(seasonRows);
    } catch (reason: unknown) {
      setReferenceError(reason instanceof Error ? reason.message : "Verified farmers, farms and seasons could not be loaded.");
    } finally {
      setReferenceLoading(false);
    }
  }, []);

  const loadSettlements = useCallback(async () => {
    setSettlementsError("");
    settlementsLoadedRef.current = false;
    try {
      const rows = await loadAllPages((options) => services.accounts.listSettlements(options));
      setSettlements(rows.filter((row) => row.status === "POSTED"));
      settlementsLoadedRef.current = true;
    } catch (reason: unknown) {
      setSettlementsError(reason instanceof Error ? reason.message : "Posted settlements could not be loaded.");
    }
  }, []);

  const ensureSettlements = useCallback(() => {
    if (!settlementsLoadedRef.current && !settlementsError) void loadSettlements();
  }, [loadSettlements, settlementsError]);

  const loadDetail = useCallback(async (advanceId: string) => {
    const generation = ++detailRequestGenerationRef.current;
    setDetailLoading(true);
    setDetailError("");
    try {
      const [advanceRow, recoveryRows] = await Promise.all([
        services.accounts.getFarmerAdvance(advanceId),
        loadAllPages(
          (options) => services.accounts.listFarmerAdvanceRecoveries(advanceId, options),
          {},
          (row) => row.id,
        ),
      ]);
      if (detailRequestGenerationRef.current !== generation) return;
      setDetail(advanceRow);
      setHistory(recoveryRows);
    } catch (reason: unknown) {
      if (detailRequestGenerationRef.current !== generation) return;
      setDetailError(reason instanceof Error ? reason.message : "Farmer advance detail could not be loaded.");
      setHistory(undefined);
    } finally {
      if (detailRequestGenerationRef.current === generation) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!readable) return;
    void loadAdvances();
    void loadReferenceData();
  }, [readable, loadAdvances, loadReferenceData]);

  const runAction = async (key: string, action: () => Promise<unknown>, successMessage: string) => {
    if (actionRef.current) return;
    actionRef.current = true;
    setBusyAction(key);
    try {
      await action();
      toast.success(successMessage);
      await reloadAdvances();
      if (selectedId) void loadDetail(selectedId);
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "The advance action could not be completed.");
    } finally {
      actionRef.current = false;
      setBusyAction("");
    }
  };

  const openDetail = (row: FarmerAdvance) => {
    setSelectedId(row.id);
    setDetail(row);
    setHistory(undefined);
    setDetailError("");
    void loadDetail(row.id);
    ensureSettlements();
  };

  const openDisburse = (row: FarmerAdvance) => {
    setDisburseTarget(row);
    setDisburseForm({ payment_method: "MOBILE_MONEY", external_payment_reference: "" });
  };

  const openCancel = (row: FarmerAdvance) => {
    setCancelTarget(row);
    setCancelReason("");
  };

  const openRecover = (row: FarmerAdvance) => {
    setRecoverTarget(row);
    setRecoverForm(emptyRecoveryForm());
    setRecoverError("");
    recoverKeyRef.current = null;
    recoverSignatureRef.current = "";
    ensureSettlements();
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (creatingRef.current) return;
    const reference = createForm.advance_reference.trim();
    const purpose = createForm.purpose.trim();
    const notes = createForm.notes.trim();
    const currency = createForm.currency.trim().toUpperCase();
    const farmer = farmers.find((row) => row.id === createForm.farmer_id);
    const farm = farms.find((row) => row.id === createForm.farm_id);
    const season = seasons.find((row) => row.id === createForm.harvest_season_id);

    if (reference.length < 3 || reference.length > 80) {
      setCreateError("Advance reference must be between 3 and 80 characters.");
      return;
    }
    if (!farmer) {
      setCreateError("Select a verified, active farmer whose record is approved.");
      return;
    }
    if (createForm.farm_id && (!farm || farm.farmerId !== farmer.id)) {
      setCreateError("The selected farm does not belong to the selected farmer.");
      return;
    }
    if (!/^[A-Z]{3}$/.test(currency)) {
      setCreateError("Currency must be a 3-letter code such as UGX.");
      return;
    }
    if (!isPositiveMoney(createForm.principal_amount)) {
      setCreateError("Principal must be a positive amount with at most 2 decimal places (maximum 100,000,000,000).");
      return;
    }
    if (purpose.length < 3 || purpose.length > 500) {
      setCreateError("Purpose is required and must be between 3 and 500 characters.");
      return;
    }
    if (notes.length > 2000) {
      setCreateError("Notes must be 2,000 characters or fewer.");
      return;
    }

    const payload: FarmerAdvanceCreateInput = {
      advance_reference: reference,
      farmer_id: farmer.id,
      farm_id: createForm.farm_id || undefined,
      harvest_season_id: season?.id,
      currency,
      principal_amount: Number(createForm.principal_amount),
      purpose,
      notes: notes || undefined,
    };
    const signature = JSON.stringify(payload);
    if (!createKeyRef.current || createSignatureRef.current !== signature) {
      createKeyRef.current = newIdempotencyKey();
      createSignatureRef.current = signature;
    }

    creatingRef.current = true;
    setCreating(true);
    setCreateError("");
    try {
      await services.accounts.createFarmerAdvance(payload, createKeyRef.current ?? undefined);
      toast.success("Farmer advance drafted");
      setShowCreate(false);
      setCreateForm(emptyCreateForm());
      createKeyRef.current = null;
      createSignatureRef.current = "";
      await reloadAdvances();
    } catch (reason: unknown) {
      setCreateError(reason instanceof Error ? reason.message : "Farmer advance could not be created.");
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  const handleDisburse = async (event: FormEvent) => {
    event.preventDefault();
    if (!disburseTarget || disbursingRef.current) return;
    const target = disburseTarget;
    const paymentMethod = disburseForm.payment_method.trim();
    const externalPaymentReference = disburseForm.external_payment_reference.trim();
    if (paymentMethod && (paymentMethod.length < 2 || paymentMethod.length > 60)) {
      toast.error("Payment method must be between 2 and 60 characters when provided.");
      return;
    }
    if (externalPaymentReference.length > 120) {
      toast.error("External payment reference must be 120 characters or fewer.");
      return;
    }
    disbursingRef.current = true;
    setDisbursing(true);
    try {
      const payload: FarmerAdvanceDisburseInput = {
        payment_method: paymentMethod || undefined,
        external_payment_reference: externalPaymentReference || undefined,
      };
      await services.accounts.disburseFarmerAdvance(target.id, payload);
      toast.success("Farmer advance disbursed");
      setDisburseTarget(undefined);
      setDisburseForm({ payment_method: "MOBILE_MONEY", external_payment_reference: "" });
      await reloadAdvances();
      if (selectedId === target.id) void loadDetail(target.id);
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Farmer advance could not be disbursed.");
    } finally {
      disbursingRef.current = false;
      setDisbursing(false);
    }
  };

  const handleCancel = async (event: FormEvent) => {
    event.preventDefault();
    if (!cancelTarget || cancellingRef.current) return;
    const target = cancelTarget;
    const reason = cancelReason.trim();
    if (!reason) {
      toast.error("A cancellation reason is required.");
      return;
    }
    if (reason.length > 1000) {
      toast.error("Cancellation reason must be 1,000 characters or fewer.");
      return;
    }
    cancellingRef.current = true;
    setCancelling(true);
    try {
      await services.accounts.cancelFarmerAdvance(target.id, reason);
      toast.success("Farmer advance cancelled");
      setCancelTarget(undefined);
      setCancelReason("");
      await reloadAdvances();
      if (selectedId === target.id) void loadDetail(target.id);
    } catch (reasonError: unknown) {
      toast.error(reasonError instanceof Error ? reasonError.message : "Farmer advance could not be cancelled.");
    } finally {
      cancellingRef.current = false;
      setCancelling(false);
    }
  };

  const handleRecover = async (event: FormEvent) => {
    event.preventDefault();
    if (!recoverTarget || recoveringRef.current) return;
    const target = recoverTarget;
    const reference = recoverForm.recovery_reference.trim();
    const notes = recoverForm.notes.trim();
    const amountRaw = recoverForm.amount.trim();
    const amount = Number(amountRaw);
    const settlement = postedSettlementsForAdvance.find((row) => row.id === recoverForm.settlement_id);

    if (reference.length < 3 || reference.length > 80) {
      setRecoverError("Recovery reference must be between 3 and 80 characters.");
      return;
    }
    if (!isValidLocalDate(recoverForm.recovery_date)) {
      setRecoverError("Recovery date must be a valid calendar date in YYYY-MM-DD format.");
      return;
    }
    if (!isPositiveMoney(amountRaw)) {
      setRecoverError("Recovery amount must be a positive amount with at most 2 decimal places.");
      return;
    }
    if (amount > target.outstandingAmount) {
      setRecoverError(
        `Recovery amount cannot exceed the outstanding balance of ${target.outstandingAmount.toLocaleString()} ${target.currency}.`,
      );
      return;
    }
    if (recoverForm.recovery_source === "SETTLEMENT" && !settlement) {
      setRecoverError("Select a posted settlement for this farmer and currency, or switch to manual repayment.");
      return;
    }
    if (notes.length > 2000) {
      setRecoverError("Notes must be 2,000 characters or fewer.");
      return;
    }

    const payload: FarmerAdvanceRecoveryCreateInput = {
      recovery_reference: reference,
      recovery_date: recoverForm.recovery_date,
      amount,
      currency: target.currency,
      recovery_source: recoverForm.recovery_source,
      settlement_id: recoverForm.recovery_source === "SETTLEMENT" ? recoverForm.settlement_id : undefined,
      notes: notes || undefined,
    };
    const signature = JSON.stringify(payload);
    if (!recoverKeyRef.current || recoverSignatureRef.current !== signature) {
      recoverKeyRef.current = newIdempotencyKey();
      recoverSignatureRef.current = signature;
    }

    recoveringRef.current = true;
    setRecovering(true);
    setRecoverError("");
    try {
      await services.accounts.recoverFarmerAdvance(target.id, payload, recoverKeyRef.current ?? undefined);
      toast.success("Advance recovery recorded");
      setRecoverTarget(undefined);
      setRecoverForm(emptyRecoveryForm());
      recoverKeyRef.current = null;
      recoverSignatureRef.current = "";
      await reloadAdvances();
      if (selectedId === target.id) void loadDetail(target.id);
      try {
        await onBalanceChange?.(target.farmerId);
      } catch {
        // The parent owns balance-refresh error reporting; a failed refresh must not invalidate the recorded recovery.
      }
    } catch (reason: unknown) {
      setRecoverError(reason instanceof Error ? reason.message : "Advance recovery could not be recorded.");
    } finally {
      recoveringRef.current = false;
      setRecovering(false);
    }
  };

  const eligibleFarms = useMemo(
    () => farms.filter((farm) => farm.farmerId === createForm.farmer_id),
    [farms, createForm.farmer_id],
  );

  const postedSettlementsForAdvance = useMemo(() => {
    if (!recoverTarget) return [];
    return settlements.filter(
      (row) =>
        row.status === "POSTED" &&
        row.farmerId === recoverTarget.farmerId &&
        (row.currency || "UGX").toUpperCase() === recoverTarget.currency.toUpperCase(),
    );
  }, [recoverTarget, settlements]);

  const selectedSettlement = postedSettlementsForAdvance.find((row) => row.id === recoverForm.settlement_id);

  const settlementLookup = useMemo(() => {
    const map = new Map<string, string>();
    settlements.forEach((row) => map.set(row.id, row.transactionReference));
    return map;
  }, [settlements]);

  const filteredAdvances = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (advances || []).filter((row) => {
      if (statusFilter !== "ALL" && row.status !== statusFilter) return false;
      if (!term) return true;
      return row.advanceReference.toLowerCase().includes(term) || (row.farmerName || "").toLowerCase().includes(term);
    });
  }, [advances, search, statusFilter]);

  const currencyTotals = useMemo(() => {
    const totals = new Map<string, { principal: number; recovered: number; outstanding: number }>();
    for (const row of advances || []) {
      const currency = row.currency || "UGX";
      const bucket = totals.get(currency) || { principal: 0, recovered: 0, outstanding: 0 };
      if (row.status === "DISBURSED" || row.status === "PARTIALLY_RECOVERED" || row.status === "RECOVERED") {
        bucket.principal += row.principalAmount;
      }
      bucket.recovered += row.netRecoveredAmount;
      bucket.outstanding += row.outstandingAmount;
      totals.set(currency, bucket);
    }
    return Array.from(totals.entries()).sort(([left], [right]) => left.localeCompare(right));
  }, [advances]);

  const closeDetail = () => {
    detailRequestGenerationRef.current += 1;
    setSelectedId(null);
    setDetail(undefined);
    setHistory(undefined);
    setDetailError("");
    setDetailLoading(false);
  };

  const updateCreateForm = (changes: Partial<ReturnType<typeof emptyCreateForm>>) => {
    setCreateForm((form) => ({ ...form, ...changes }));
  };

  const updateRecoverForm = (changes: Partial<ReturnType<typeof emptyRecoveryForm>>) => {
    setRecoverForm((form) => ({ ...form, ...changes }));
  };

  if (!readable) {
    return (
      <Card data-testid="farmer-advances-no-access">
        <CardContent className="p-6 text-sm text-muted-foreground">
          You do not have permission to view farmer advances. Accounts, payment or transaction view permission is required.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="farmer-advances-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold">Farmer advances</p>
          <p className="text-sm text-muted-foreground">
            Advances issued to verified farmers with an append-only recovery history. Advances are recouped from farmer money and are never programme expenses.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            data-testid="advance-search"
            className="w-64"
            placeholder="Search reference or farmer..."
            value={search}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
          />
          <select
            data-testid="advance-status-filter"
            aria-label="Filter advances by status"
            className={`${SELECT_CLASS} w-48`}
            value={statusFilter}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => setStatusFilter(event.target.value)}
          >
            {STATUS_FILTERS.map((status) => (
              <option key={status} value={status}>{status === "ALL" ? "All statuses" : status}</option>
            ))}
          </select>
          {canPrepare ? (
            <Button
              type="button"
              data-testid="advance-create-open"
              onClick={() => {
                setShowCreate(true);
                setCreateError("");
                if (referenceError) void loadReferenceData();
              }}
            >
              <Plus className="mr-1 size-4" />New advance
            </Button>
          ) : null}
        </div>
      </div>

      {currencyTotals.length > 0 ? (
        <div className="flex flex-wrap gap-3" data-testid="advance-currency-totals">
          {currencyTotals.map(([currency, totals]) => (
            <Card key={currency} data-testid={`advance-totals-${currency}`}>
              <CardContent className="p-3 text-sm">
                <span className="font-semibold">{currency}</span>{" "}
                Disbursed {totals.principal.toLocaleString()} · Recovered {totals.recovered.toLocaleString()} · Outstanding{" "}
                {totals.outstanding.toLocaleString()}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      {advances === undefined && !loadError ? (
        <LoadingState label="Loading farmer advances" />
      ) : loadError && !advances ? (
        <div data-testid="advance-list-error">
          <ErrorState message={loadError} onRetry={() => { void loadAdvances(); }} />
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Farmer</TableHead>
                  <TableHead>Farm</TableHead>
                  <TableHead>Season</TableHead>
                  <TableHead className="text-right">Principal</TableHead>
                  <TableHead className="text-right">Net recovered</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAdvances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="py-8 text-center text-muted-foreground">
                      No farmer advances match this view.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAdvances.map((row) => (
                    <TableRow key={row.id} data-testid={`advance-row-${row.id}`}>
                      <TableCell className="font-mono text-sm">{row.advanceReference}</TableCell>
                      <TableCell>{formatDate(row.requestedAt || row.createdAt)}</TableCell>
                      <TableCell>{row.farmerName || "Unknown farmer"}</TableCell>
                      <TableCell>{row.farmName || "—"}</TableCell>
                      <TableCell>{row.seasonName ? `${row.seasonCode ? `${row.seasonCode} — ` : ""}${row.seasonName}` : row.seasonCode || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{row.principalAmount.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono">{row.netRecoveredAmount.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{row.outstandingAmount.toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={ADVANCE_STATUS_COLORS[row.status] || ""}>{row.status}</Badge>
                      </TableCell>
                      <TableCell>{row.currency}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Button type="button" size="sm" variant="ghost" data-testid="advance-action-view" onClick={() => openDetail(row)}>
                            View
                          </Button>
                          {canApprove && row.status === "DRAFT" ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              data-testid="advance-action-approve"
                              disabled={busyAction === `approve-${row.id}`}
                              onClick={() =>
                                void runAction(`approve-${row.id}`, () => services.accounts.approveFarmerAdvance(row.id), "Farmer advance approved")
                              }
                            >
                              Approve
                            </Button>
                          ) : null}
                          {canApprove && row.status === "APPROVED" ? (
                            <Button type="button" size="sm" data-testid="advance-action-disburse" onClick={() => openDisburse(row)}>
                              Disburse
                            </Button>
                          ) : null}
                          {canApprove && (row.status === "DRAFT" || row.status === "APPROVED") ? (
                            <Button type="button" size="sm" variant="outline" data-testid="advance-action-cancel" onClick={() => openCancel(row)}>
                              Cancel
                            </Button>
                          ) : null}
                          {canApprove && (row.status === "DISBURSED" || row.status === "PARTIALLY_RECOVERED") ? (
                            <Button type="button" size="sm" variant="outline" data-testid="advance-action-recover" onClick={() => openRecover(row)}>
                              Recover
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {showCreate ? (
        <Modal
          title="New farmer advance"
          description="Draft an advance for a verified, active farmer. Approval and disbursement are separate controlled steps."
          testId="advance-create-modal"
          onClose={() => { if (!creating) setShowCreate(false); }}
          busy={creating}
        >
          <form data-testid="advance-create-form" onSubmit={handleCreate} className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label htmlFor="advance-create-reference">Advance reference</Label>
                <Input
                  id="advance-create-reference"
                  data-testid="advance-create-reference"
                  value={createForm.advance_reference}
                  minLength={3}
                  maxLength={80}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => updateCreateForm({ advance_reference: event.target.value })}
                  placeholder="e.g. ADV-2026-0001"
                  required
                />
              </div>
              <div>
                <Label htmlFor="advance-create-currency">Currency</Label>
                <Input
                  id="advance-create-currency"
                  data-testid="advance-create-currency"
                  value={createForm.currency}
                  maxLength={3}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => updateCreateForm({ currency: event.target.value.toUpperCase() })}
                  placeholder="UGX"
                />
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label htmlFor="advance-create-farmer">Verified farmer</Label>
                <select
                  id="advance-create-farmer"
                  data-testid="advance-create-farmer"
                  className={SELECT_CLASS}
                  value={createForm.farmer_id}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                    updateCreateForm({ farmer_id: event.target.value, farm_id: "" })
                  }
                  required
                >
                  <option value="">Select a verified, active farmer…</option>
                  {farmers.map((farmer) => (
                    <option key={farmer.id} value={farmer.id}>
                      {`${farmer.firstName} ${farmer.lastName} (${farmer.code})`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="advance-create-farm">Farm (optional)</Label>
                <select
                  id="advance-create-farm"
                  data-testid="advance-create-farm"
                  className={SELECT_CLASS}
                  value={createForm.farm_id}
                  disabled={!createForm.farmer_id || eligibleFarms.length === 0}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) => updateCreateForm({ farm_id: event.target.value })}
                >
                  <option value="">No specific farm</option>
                  {eligibleFarms.map((farm) => (
                    <option key={farm.id} value={farm.id}>{farm.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label htmlFor="advance-create-season">Harvest season (optional)</Label>
                <select
                  id="advance-create-season"
                  data-testid="advance-create-season"
                  className={SELECT_CLASS}
                  value={createForm.harvest_season_id}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) => updateCreateForm({ harvest_season_id: event.target.value })}
                >
                  <option value="">No specific season</option>
                  {seasons.map((season) => (
                    <option key={season.id} value={season.id}>{`${season.seasonCode} — ${season.seasonName}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="advance-create-principal">Principal amount</Label>
                <Input
                  id="advance-create-principal"
                  data-testid="advance-create-principal"
                  type="number"
                  min="0.01"
                  max="100000000000"
                  step="0.01"
                  value={createForm.principal_amount}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => updateCreateForm({ principal_amount: event.target.value })}
                  placeholder="e.g. 300000"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="advance-create-purpose">Purpose</Label>
              <Input
                id="advance-create-purpose"
                data-testid="advance-create-purpose"
                value={createForm.purpose}
                maxLength={500}
                onChange={(event: ChangeEvent<HTMLInputElement>) => updateCreateForm({ purpose: event.target.value })}
                placeholder="e.g. Fertiliser and labour before harvest"
                required
              />
            </div>
            <div>
              <Label htmlFor="advance-create-notes">Notes (optional)</Label>
              <Textarea
                id="advance-create-notes"
                data-testid="advance-create-notes"
                value={createForm.notes}
                maxLength={2000}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => updateCreateForm({ notes: event.target.value })}
              />
            </div>
            {referenceLoading ? <p className="text-xs text-muted-foreground">Loading verified farmers, farms and seasons…</p> : null}
            {referenceError ? (
              <p className="text-xs text-destructive" data-testid="advance-reference-error">
                {referenceError}{" "}
                <button type="button" className="underline" onClick={() => { void loadReferenceData(); }}>Retry</button>
              </p>
            ) : null}
            {createError ? (
              <p role="alert" data-testid="advance-create-error" className="text-sm text-destructive">{createError}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
              <Button type="submit" data-testid="advance-create-submit" disabled={creating || referenceLoading}>
                {creating ? "Saving…" : "Save draft advance"}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}

      {disburseTarget ? (
        <Modal
          title={`Disburse ${disburseTarget.advanceReference}`}
          description={`${disburseTarget.farmerName || "Farmer"} · ${disburseTarget.principalAmount.toLocaleString()} ${disburseTarget.currency}`}
          testId="advance-disburse-modal"
          onClose={() => { if (!disbursing) setDisburseTarget(undefined); }}
          busy={disbursing}
        >
          <form data-testid="advance-disburse-form" onSubmit={handleDisburse} className="space-y-3">
            <div>
              <Label htmlFor="advance-disburse-method">Payment method (optional)</Label>
              <Input
                id="advance-disburse-method"
                data-testid="advance-disburse-method"
                value={disburseForm.payment_method}
                minLength={2}
                maxLength={60}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setDisburseForm((form) => ({ ...form, payment_method: event.target.value }))}
                placeholder="e.g. MOBILE_MONEY, BANK_TRANSFER, CASH"
              />
            </div>
            <div>
              <Label htmlFor="advance-disburse-reference">External payment reference (optional)</Label>
              <Input
                id="advance-disburse-reference"
                data-testid="advance-disburse-reference"
                value={disburseForm.external_payment_reference}
                maxLength={120}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setDisburseForm((form) => ({ ...form, external_payment_reference: event.target.value }))
                }
                placeholder="Provider or bank reference"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDisburseTarget(undefined)} disabled={disbursing}>Cancel</Button>
              <Button type="submit" data-testid="advance-disburse-submit" disabled={disbursing}>
                {disbursing ? "Disbursing…" : "Mark disbursed"}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}

      {cancelTarget ? (
        <Modal
          title={`Cancel ${cancelTarget.advanceReference}`}
          description={`${cancelTarget.farmerName || "Farmer"} · ${cancelTarget.principalAmount.toLocaleString()} ${cancelTarget.currency}`}
          testId="advance-cancel-modal"
          onClose={() => { if (!cancelling) setCancelTarget(undefined); }}
          busy={cancelling}
        >
          <form data-testid="advance-cancel-form" onSubmit={handleCancel} className="space-y-3">
            <div>
              <Label htmlFor="advance-cancel-reason">Cancellation reason</Label>
              <Textarea
                id="advance-cancel-reason"
                data-testid="advance-cancel-reason"
                value={cancelReason}
                maxLength={1000}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setCancelReason(event.target.value)}
                placeholder="Why is this advance being cancelled?"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCancelTarget(undefined)} disabled={cancelling}>Keep advance</Button>
              <Button type="submit" variant="destructive" data-testid="advance-cancel-submit" disabled={cancelling || !cancelReason.trim()}>
                {cancelling ? "Cancelling…" : "Cancel advance"}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}

      {recoverTarget ? (
        <Modal
          title={`Recover advance ${recoverTarget.advanceReference}`}
          description={`${recoverTarget.farmerName || "Farmer"} · principal ${recoverTarget.principalAmount.toLocaleString()} ${recoverTarget.currency} · outstanding ${recoverTarget.outstandingAmount.toLocaleString()} ${recoverTarget.currency}`}
          testId="advance-recover-modal"
          onClose={() => { if (!recovering) setRecoverTarget(undefined); }}
          busy={recovering}
        >
          <form data-testid="advance-recover-form" onSubmit={handleRecover} className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label htmlFor="advance-recover-date">Recovery date</Label>
                <Input
                  id="advance-recover-date"
                  data-testid="advance-recover-date"
                  type="date"
                  value={recoverForm.recovery_date}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => updateRecoverForm({ recovery_date: event.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="advance-recover-reference">Recovery reference</Label>
                <Input
                  id="advance-recover-reference"
                  data-testid="advance-recover-reference"
                  value={recoverForm.recovery_reference}
                  minLength={3}
                  maxLength={80}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => updateRecoverForm({ recovery_reference: event.target.value })}
                  placeholder="e.g. RCV-2026-0001"
                  required
                />
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <Label htmlFor="advance-recover-amount">Amount</Label>
                <Input
                  id="advance-recover-amount"
                  data-testid="advance-recover-amount"
                  type="number"
                  min="0.01"
                  max={recoverTarget.outstandingAmount}
                  step="0.01"
                  value={recoverForm.amount}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => updateRecoverForm({ amount: event.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="advance-recover-currency">Currency</Label>
                <Input id="advance-recover-currency" data-testid="advance-recover-currency" value={recoverTarget.currency} readOnly />
              </div>
              <div>
                <Label htmlFor="advance-recover-source">Recovery source</Label>
                <select
                  id="advance-recover-source"
                  data-testid="advance-recover-source"
                  className={SELECT_CLASS}
                  value={recoverForm.recovery_source}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                    updateRecoverForm({
                      recovery_source: event.target.value as FarmerAdvanceRecoverySource,
                      settlement_id: event.target.value === "SETTLEMENT" ? recoverForm.settlement_id : "",
                    })
                  }
                >
                  {RECOVERY_SOURCES.map((source) => (
                    <option key={source.value} value={source.value}>{source.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {recoverForm.recovery_source === "SETTLEMENT" ? (
              <div>
                <Label htmlFor="advance-recover-settlement">Posted settlement</Label>
                <select
                  id="advance-recover-settlement"
                  data-testid="advance-recover-settlement"
                  className={SELECT_CLASS}
                  value={recoverForm.settlement_id}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) => updateRecoverForm({ settlement_id: event.target.value })}
                  required
                >
                  <option value="">Select a posted settlement…</option>
                  {postedSettlementsForAdvance.map((row) => (
                    <option key={row.id} value={row.id}>
                      {`${row.transactionReference} — ${formatDate(row.transactionDate)}`}
                    </option>
                  ))}
                </select>
                {settlementsError ? (
                  <p className="mt-1 text-xs text-destructive" data-testid="advance-settlements-error">
                    {settlementsError}{" "}
                    <button type="button" className="underline" onClick={() => { void loadSettlements(); }}>Retry</button>
                  </p>
                ) : null}
                {!settlementsError && postedSettlementsForAdvance.length === 0 ? (
                  <p className="mt-1 text-xs text-amber-600" data-testid="advance-no-settlements">
                    No posted settlements exist for this farmer and currency yet. Use manual repayment if the farmer repaid directly.
                  </p>
                ) : null}
                {selectedSettlement ? (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="advance-settlement-entitlement">
                    Settlement {selectedSettlement.transactionReference}: gross value{" "}
                    {(selectedSettlement.grossAmount || 0).toLocaleString()} {selectedSettlement.currency}; farmer payable{" "}
                    {(selectedSettlement.farmerPayableAmount || 0).toLocaleString()} {selectedSettlement.currency}. Remaining
                    recoverable capacity is the advance outstanding balance ({recoverTarget.outstandingAmount.toLocaleString()}{" "}
                    {recoverTarget.currency}) and is checked on save — it cannot be derived from the settlement gross amount.
                  </p>
                ) : null}
              </div>
            ) : null}
            <div>
              <Label htmlFor="advance-recover-notes">Notes (optional)</Label>
              <Textarea
                id="advance-recover-notes"
                data-testid="advance-recover-notes"
                value={recoverForm.notes}
                maxLength={2000}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => updateRecoverForm({ notes: event.target.value })}
              />
            </div>
            {recoverError ? (
              <p role="alert" data-testid="advance-recover-error" className="text-sm text-destructive">{recoverError}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setRecoverTarget(undefined)} disabled={recovering}>Cancel</Button>
              <Button type="submit" data-testid="advance-recover-submit" disabled={recovering}>
                {recovering ? "Recording…" : "Record recovery"}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}

      {selectedId && detail ? (
        <Modal title={`Advance ${detail.advanceReference}`} testId="advance-detail-modal" onClose={closeDetail}>
          {detailError ? (
            <div data-testid="advance-detail-error">
              <ErrorState message={detailError} onRetry={() => { void loadDetail(selectedId); }} />
            </div>
          ) : null}
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <div>
              <p className="text-muted-foreground">Farmer</p>
              <Link className="font-medium underline" to={`/farmers/${detail.farmerId}`}>
                {detail.farmerName || "Unknown farmer"}
              </Link>
            </div>
            <div>
              <p className="text-muted-foreground">Status</p>
              <Badge variant="outline" className={ADVANCE_STATUS_COLORS[detail.status] || ""}>{detail.status}</Badge>
            </div>
            <div>
              <p className="text-muted-foreground">Farm</p>
              <p>{detail.farmName || "Not linked to a specific farm"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Season</p>
              <p>{detail.seasonName ? `${detail.seasonCode ? `${detail.seasonCode} — ` : ""}${detail.seasonName}` : detail.seasonCode || "Not linked to a season"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Principal</p>
              <p className="font-mono">{detail.principalAmount.toLocaleString()} {detail.currency}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Net recovered</p>
              <p className="font-mono">{detail.netRecoveredAmount.toLocaleString()} {detail.currency}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Outstanding</p>
              <p className="font-mono font-semibold">{detail.outstandingAmount.toLocaleString()} {detail.currency}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Gross recovered / reversed</p>
              <p className="font-mono">{detail.grossRecoveredAmount.toLocaleString()} / {detail.reversedRecoveryAmount.toLocaleString()}</p>
            </div>
          </div>
          <div className="space-y-1 text-sm">
            <p><span className="text-muted-foreground">Purpose:</span> {detail.purpose}</p>
            {detail.notes ? <p><span className="text-muted-foreground">Notes:</span> {detail.notes}</p> : null}
            {detail.cancellationReason ? <p><span className="text-muted-foreground">Cancellation reason:</span> {detail.cancellationReason}</p> : null}
          </div>
          <dl className="grid gap-2 text-xs text-muted-foreground md:grid-cols-2">
            <div><dt>Requested at</dt><dd>{formatDateTime(detail.requestedAt)}</dd></div>
            <div><dt>Approved at</dt><dd>{formatDateTime(detail.approvedAt)}</dd></div>
            <div><dt>Disbursed at</dt><dd>{formatDateTime(detail.disbursedAt)}</dd></div>
            <div><dt>Cancelled at</dt><dd>{formatDateTime(detail.cancelledAt)}</dd></div>
            <div><dt>Payment method</dt><dd>{detail.paymentMethod || "Not recorded"}</dd></div>
            <div><dt>External payment reference</dt><dd>{detail.externalPaymentReference || "Not recorded"}</dd></div>
            <div><dt>Finance payment request</dt><dd className="font-mono">{detail.financePaymentRequestId || "Not linked"}</dd></div>
            <div><dt>Last updated</dt><dd>{formatDateTime(detail.updatedAt)}</dd></div>
          </dl>
          <div className="space-y-2">
            <p className="font-semibold">Recovery history (append-only)</p>
            {detailLoading && history === undefined ? (
              <p className="text-sm text-muted-foreground">Loading recovery history…</p>
            ) : (
              <Table data-testid="advance-recovery-history">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Settlement</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(history || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-4 text-center text-muted-foreground">No recoveries recorded yet.</TableCell>
                    </TableRow>
                  ) : (
                    (history || []).map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{entry.recoveryDate}</TableCell>
                        <TableCell className="font-mono text-xs">{entry.recoveryReference}</TableCell>
                        <TableCell>{entry.recoverySource}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {entry.settlementId ? settlementLookup.get(entry.settlementId) || entry.settlementId : "—"}
                        </TableCell>
                        <TableCell className="text-right font-mono">{entry.amount.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={entry.entryType === "REVERSAL" ? "border-red-500/20 bg-red-500/10 text-red-600" : ""}>
                            {entry.entryType}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{entry.notes || (entry.reversalOfId ? `Reversal of ${entry.reversalOfId}` : "—")}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
          {canApprove && (detail.status === "DISBURSED" || detail.status === "PARTIALLY_RECOVERED") ? (
            <Button type="button" data-testid="advance-detail-recover" onClick={() => openRecover(detail)}>Record recovery</Button>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}

export default FarmerAdvancesTab;
